import { memo } from 'react'
import { Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { VideoItem } from '@/types/video'

export type { VideoItem }

interface VideoCardProps {
  video: VideoItem
  onPlay: (video: VideoItem) => void
}

/**
 * Memoized video card to prevent re-renders in gallery grid.
 * Only re-renders when video data or onPlay callback changes.
 */
export const VideoCard = memo(function VideoCard({ video, onPlay }: VideoCardProps) {
  const aspectRatio = video.dimensions.width / video.dimensions.height
  const isPortrait = aspectRatio < 1
  const isLandscape = aspectRatio > 1.5

  return (
    <div
      className={cn(
        'relative rounded-xl p-[1px] transition-all duration-300 hover:scale-[1.02] hover:z-10',
        'bg-gradient-to-br from-white/[0.12] via-white/[0.04] to-white/[0.08]',
        isPortrait && 'row-span-2',
        isLandscape && 'col-span-2'
      )}
      onClick={() => onPlay(video)}
    >
      <div className="group relative rounded-xl overflow-hidden cursor-pointer h-full">
        <img
          src={video.thumbnail}
          alt={video.title}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <VideoOverlay title={video.title} />
      </div>
    </div>
  )
})

const VideoOverlay = memo(function VideoOverlay({ title }: { title: string }) {
  return (
    <>
      {/* Play button overlay */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
        <div className="p-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 scale-75 group-hover:scale-100 transition-transform duration-300">
          <Play className="size-6 text-white fill-white" />
        </div>
      </div>
      {/* Title gradient */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-12">
        <h3 className="font-medium text-sm text-white truncate">{title}</h3>
      </div>
    </>
  )
})
